import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listStoryboards,
  createStoryboard,
  getStoryboard,
  updateStoryboard,
  deleteStoryboard,
  type CreateStoryboardInput,
  type UpdateStoryboardInput,
  type StoryboardAsset,
} from '../storyboards.service'

const STORYBOARDS_QUERY_KEY = 'storyboards'

export interface UseStoryboardsParams {
  limit?: number
  cursor?: string
  q?: string
}

export function useStoryboards(params?: UseStoryboardsParams) {
  return useQuery({
    queryKey: [STORYBOARDS_QUERY_KEY, 'list', params],
    queryFn: () => listStoryboards(params),
    staleTime: 30000,
  })
}

export function useStoryboard(id: string | undefined) {
  return useQuery({
    queryKey: [STORYBOARDS_QUERY_KEY, 'detail', id],
    queryFn: () => getStoryboard(id!),
    enabled: !!id && id !== 'new',
    staleTime: 30000,
  })
}

export function useCreateStoryboard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateStoryboardInput) => createStoryboard(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STORYBOARDS_QUERY_KEY, 'list'] })
    },
  })
}

export function useUpdateStoryboard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateStoryboardInput & { id: string }) =>
      updateStoryboard(id, input),
    onSuccess: (data: StoryboardAsset) => {
      queryClient.invalidateQueries({ queryKey: [STORYBOARDS_QUERY_KEY, 'list'] })
      queryClient.setQueryData([STORYBOARDS_QUERY_KEY, 'detail', data.id], data)
    },
  })
}

export function useDeleteStoryboard() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteStoryboard(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: [STORYBOARDS_QUERY_KEY, 'list'] })
      queryClient.removeQueries({ queryKey: [STORYBOARDS_QUERY_KEY, 'detail', id] })
    },
  })
}
